/** The one place the `/help` command descriptions live. Each adapter owns only its header, intro line,
 *  emphasis markers and the ordered list of commands it actually exposes — the per-command wording is
 *  shared here so a command can never be listed on one surface/language and silently dropped on another
 *  (the exact drift that left Telegram's Czech help without `/context`).
 *
 *  Placeholders let one string serve every surface's noun for the conversation container:
 *   - `{place}`    — accusative/nominative ("channel" / "chat" · "kanál" / "chat")
 *   - `{placeLoc}` — Czech locative after "v tomto" ("kanálu" / "chatu"); English has no case, so it
 *                    resolves to the same word as `{place}`. */
export const HELP_DESCRIPTIONS = {
  en: {
    model: 'pick the AI model for this {place}',
    context: 'continue this {place} in one of your conversations',
    project: 'switch this {place} to one of your projects',
    reasoning: 'set the reasoning effort for this {place}',
    fast: 'set your account Fast preference',
    display: 'configure live tools and answer delivery here',
    new: 'start a fresh conversation here',
    stop: 'stop the running agent',
    clear: 'clear this conversation and start from an empty context',
    goal: 'create, inspect, pause, resume or clear a persistent goal',
    rename: 'rename this conversation',
    stats: 'model, context and usage',
    plan: 'think through the approach before editing',
    build: 'implement changes with tools',
    workflow: 'coordinate work across delegated agents',
    compact: 'summarize to free up context',
    restart: 'restart the Chetty daemon (admin)',
    help: 'this message',
  },
  cs: {
    model: 'výběr AI modelu pro tento {place}',
    context: 'navázat v tomto {placeLoc} na jednu ze svých konverzací',
    project: 'přepnout tento {place} na jeden ze svých projektů',
    reasoning: 'úroveň uvažování pro tento {place}',
    fast: 'nastavit Fast režim svého účtu',
    display: 'nastavit živé nástroje a doručení odpovědi',
    new: 'začít novou konverzaci',
    stop: 'zastavit běžícího agenta',
    clear: 'vymazat tuto konverzaci a začít s prázdným kontextem',
    goal: 'vytvořit, zobrazit, pozastavit, obnovit nebo zrušit trvalý cíl',
    rename: 'přejmenovat tuto konverzaci',
    stats: 'model, kontext a využití',
    plan: 'promyslet postup před úpravami',
    build: 'provést změny pomocí nástrojů',
    workflow: 'koordinovat práci delegovaných agentů',
    compact: 'sesumarizovat a uvolnit kontext',
    restart: 'restart démona Chettyho (admin)',
    help: 'tato zpráva',
  },
  sk: {
    model: 'výber AI modelu pre tento {place}',
    context: 'nadviazať v tomto {placeLoc} na jednu zo svojich konverzácií',
    project: 'prepnúť tento {place} na jeden zo svojich projektov',
    reasoning: 'úroveň uvažovania pre tento {place}',
    fast: 'nastaviť Fast režim svojho účtu',
    display: 'nastaviť živé nástroje a doručenie odpovede',
    new: 'začať novú konverzáciu',
    stop: 'zastaviť bežiaceho agenta',
    clear: 'vymazať túto konverzáciu a začať s prázdnym kontextom',
    goal: 'vytvoriť, zobraziť, pozastaviť, obnoviť alebo zrušiť trvalý cieľ',
    rename: 'premenovať túto konverzáciu',
    stats: 'model, kontext a využitie',
    plan: 'premyslieť postup pred úpravami',
    build: 'vykonať zmeny pomocou nástrojov',
    workflow: 'koordinovať prácu delegovaných agentov',
    compact: 'sumarizovať a uvoľniť kontext',
    restart: 'reštart démona Chettyho (admin)',
    help: 'táto správa',
  },
};

/** Render the command lines of a `/help` body. `commands` is the ordered list the surface exposes, each
 *  `{ name, description }` (as served by `ctx.chatCommands()` plus any adapter-local command). A built-in
 *  (or display) keyed in `HELP_DESCRIPTIONS` renders its localized wording; anything else — a plugin
 *  prompt command — falls back to its own English `description`, so the list can never drift from what the
 *  surface actually registered. `mono` wraps a command token in the surface's inline-code style (backticks
 *  on Discord/WhatsApp, identity on plain-text Telegram); `place`/`placeLoc` fill the container-noun
 *  placeholders. */
export function renderHelpLines({ lang, commands, mono, place, placeLoc = place }) {
  return commands.map(command =>
    `${mono(`/${command.name}`)} — ${helpCommandDescription({ lang, command, place, placeLoc })}`);
}

/** The same description projection for structured menus and plain-text help. Plugin commands retain
 * their declared description; core command wording never gets copied into a surface dictionary. */
export function helpCommandDescription({ lang, command, place, placeLoc = place }) {
  const descriptions = HELP_DESCRIPTIONS[lang] ?? HELP_DESCRIPTIONS.en;
  const localized = descriptions[command.name];
  return localized
    ? localized.replaceAll('{placeLoc}', placeLoc).replaceAll('{place}', place)
    : (command.description ?? '');
}
